import { z } from 'zod';

import { ByokProvider } from './ai-byok.dto';
import {
  BulkPlanResultSchema,
  LocalDateSchema,
  MyWorkItemSchema,
  PlanScopeSchema,
} from './my-work.dto';
import { WeekStartSchema } from './users';

/**
 * ✨ AI planner — Personal tier (docs/release-1/17-V2-BUILD-PLAN.md §7 W6-0 /
 * W6-B, "W6 AI-core contract"). Two layers in one file:
 *   1. app ⇄ web: `/api/me/ai/*` (camelCase);
 *   2. app ⇄ AI core: the exact wire bodies of `/v1/plan-day`,
 *      `/v1/weekly-review`, `/v1/standup-draft` (snake_case; `/v1/digest` is
 *      reused unchanged — see the doc section).
 *
 * Privacy (§2.2 P12 / G9) — every call here carries personal content:
 *  - runs under MY PERSONAL team: personal BYOK key, personal budget,
 *    `ai_action_log.team_id` = personal team; the `x-projoint-active-team-id`
 *    header is IGNORED on `/api/me/ai/*`;
 *  - the context builder only reads teams where I hold `ai.use` AND the team's
 *    AI is on (+ Personal); others are summarised in `excluded`, never sent;
 *  - private notes are never sent; no MCP callback PAT is minted (the app
 *    sends all context, AI core never calls back for these features);
 *  - nothing is written to my plan by AI: a proposal is L2 and each item is
 *    accepted explicitly (`accept`), through the normal plan service;
 *  - routes: browser JWT only (no PATs), `@DisallowImpersonation()`.
 *
 * Entitlement: Personal plan or a paid team seat (`ai_planner`, entitlements.dto).
 */

// ── 1. App ⇄ web: /api/me/ai/* ──────────────────────────────────────────────

export const PlanHorizonSchema = z.enum(['day', 'week']);
export type PlanHorizon = z.infer<typeof PlanHorizonSchema>;

export const AI_PLANNER_LIMITS = {
  /** Candidate tasks sent to AI core per proposal (most relevant first). */
  candidatesMax: 150,
  /** Items a proposal may hold. */
  itemsMaxDay: 12,
  itemsMaxWeek: 40,
  /** Characters of one item's reason. */
  reasonMax: 160,
  /** A proposal can be accepted for this long (Redis TTL). */
  proposalTtlHours: 24,
  /** Characters of a generated text (weekly review, standup draft). */
  textMax: 1500,
} as const;

/** What a proposal may suggest: a day, "this week" (week horizon only) or Later. Never `unplanned`. */
export const ProposalScopeSchema = z.enum(['day', 'week', 'later']);
export type ProposalScope = z.infer<typeof ProposalScopeSchema>;

/** A team the planner did NOT read, and how many of my open tasks it holds. */
export const AiExcludedTeamSchema = z.object({
  teamId: z.string().uuid(),
  teamName: z.string(),
  count: z.number().int().min(0),
  /** `team_ai_off`: the team turned AI off / has no AI plan; `no_ai_use`: my role lacks `ai.use`. */
  reason: z.enum(['team_ai_off', 'no_ai_use']),
});
export type AiExcludedTeam = z.infer<typeof AiExcludedTeamSchema>;

export const PlanProposalItemSchema = z.object({
  taskId: z.string().uuid(),
  scope: ProposalScopeSchema,
  /** day: that day (today … end of the horizon); week: the week start; later: null. */
  planDate: LocalDateSchema.nullable(),
  /** 0-based position inside its target list (same scope + planDate); accepted items land in this order. */
  order: z.number().int().min(0),
  /** Why — shown next to the item ("Deadline Thu, 2 h left"). */
  reason: z.string().max(AI_PLANNER_LIMITS.reasonMax),
  /** The row as My Work shows it now (title, team, deadline, current plan) — no extra fetch. */
  item: MyWorkItemSchema,
});
export type PlanProposalItem = z.infer<typeof PlanProposalItemSchema>;

/**
 * POST /api/me/ai/plan → PlanProposal (201). L2: nothing is planned until
 * `accept`. Only ids from my own My Work candidates ever appear (the server
 * drops anything else AI core returns — `acceptablePlanDayItems`).
 */
export const PlanProposalSchema = z.object({
  id: z.string().uuid(),
  horizon: PlanHorizonSchema,
  /** The `today` it was made for. */
  today: LocalDateSchema,
  generatedAt: z.string(),
  expiresAt: z.string(),
  items: z.array(PlanProposalItemSchema).max(AI_PLANNER_LIMITS.itemsMaxWeek),
  excluded: z.array(AiExcludedTeamSchema),
  /** One line from AI core ("Heavy day: 2 deadlines, 7 h for 6 h"); may be null. */
  summary: z.string().max(300).nullable(),
});
export type PlanProposal = z.infer<typeof PlanProposalSchema>;

export const CreatePlanProposalSchema = z
  .object({
    horizon: PlanHorizonSchema,
    today: LocalDateSchema,
    /** Stable retry key. A replay returns the durable ready proposal without another model call. */
    clientRequestId: z.string().uuid().optional(),
  })
  .strict();
export type CreatePlanProposalInput = z.infer<typeof CreatePlanProposalSchema>;

/**
 * POST /api/me/ai/plan/:id/accept → BulkPlanResult. Accept per item: only the
 * listed ids are planned, with the proposal's scope / planDate / order.
 * Ids not in the proposal, now invisible, or with a day before `today` come
 * back in `skipped`. Unknown / expired / foreign proposal → 404
 * `plan_proposal_not_found`. `today` as in every plan write.
 */
export const AcceptPlanProposalSchema = z
  .object({
    taskIds: z.array(z.string().uuid()).min(1).max(AI_PLANNER_LIMITS.itemsMaxWeek),
    today: LocalDateSchema,
  })
  .strict()
  .refine((v) => new Set(v.taskIds).size === v.taskIds.length, {
    message: 'taskIds must be unique',
    path: ['taskIds'],
  });
export type AcceptPlanProposalInput = z.infer<typeof AcceptPlanProposalSchema>;
export const AcceptPlanProposalResultSchema = BulkPlanResultSchema;

/** POST /api/me/ai/digest[?refresh=1] — one cross-team digest per local day (cached; refresh regenerates). */
export const MeDigestRequestSchema = z.object({ today: LocalDateSchema }).strict();
export type MeDigestRequest = z.infer<typeof MeDigestRequestSchema>;

export const MeDigestSchema = z.object({
  today: LocalDateSchema,
  generatedAt: z.string(),
  /** true when served from the per-day cache (no AI call, no cost). */
  cached: z.boolean(),
  digest: z.object({
    greeting: z.string(),
    paragraph: z.string(),
    suggestedAction: z.string().nullable(),
    sources: z.array(z.string()),
    highlights: z.array(
      z.object({
        kind: z.enum(['risk', 'deadline', 'attention', 'win', 'info']),
        text: z.string(),
        /** A task key ("WEB-45") or null. */
        ref: z.string().nullable(),
      }),
    ),
    confidence: z.number().min(0).max(1),
  }),
  excluded: z.array(AiExcludedTeamSchema),
});
export type MeDigest = z.infer<typeof MeDigestSchema>;

/** POST /api/me/ai/weekly-review[?refresh=1] — `weekOf` = any day of the week reviewed (cached per week). */
export const WeeklyReviewRequestSchema = z.object({ weekOf: LocalDateSchema }).strict();
export type WeeklyReviewRequest = z.infer<typeof WeeklyReviewRequestSchema>;

/**
 * My week in numbers, over included teams + Personal only (the same set the
 * AI saw, so text and numbers agree). Mine = §2.3 `isMine`; local dates in my
 * time zone; week = `weekRange(weekOf, weekStart)`.
 */
export const WeeklyReviewStatsSchema = z.object({
  /** Mine tasks done in the week (team done or private "my part done"), by task. */
  doneCount: z.number().int().min(0),
  /** Tasks with a day plan inside the week, or a week plan for it. */
  plannedCount: z.number().int().min(0),
  /** …of which done. */
  plannedDoneCount: z.number().int().min(0),
  /** `isCarriedOver` at the end of the week (or now, for the current week). */
  carriedOverCount: z.number().int().min(0),
  /** Open plans with `rolloverCount ≥ 1` dated inside the week. */
  slippedCount: z.number().int().min(0),
  /** Mine, Open, deadline passed. */
  overdueCount: z.number().int().min(0),
  /** My time entries in the week, hours (1 decimal). */
  hoursLogged: z.number().min(0),
  /** Σ estimates of the week's day plans, hours; null when none is estimated. */
  hoursPlanned: z.number().min(0).nullable(),
  /** Mine, Open, deadline in the 7 days after the week. */
  upcomingDeadlinesCount: z.number().int().min(0),
});
export type WeeklyReviewStats = z.infer<typeof WeeklyReviewStatsSchema>;

export const WeeklyReviewSchema = z.object({
  weekStart: LocalDateSchema,
  weekEnd: LocalDateSchema,
  generatedAt: z.string(),
  cached: z.boolean(),
  text: z.string().max(AI_PLANNER_LIMITS.textMax),
  stats: WeeklyReviewStatsSchema,
  excluded: z.array(AiExcludedTeamSchema),
});
export type WeeklyReview = z.infer<typeof WeeklyReviewSchema>;

/**
 * POST /api/me/ai/standup-draft → StandupDraft. ONE team (where I'm an active
 * member); only that team's tasks are read. The text is returned, never
 * posted: I post it through the normal composer (the only plan → team bridge,
 * and user-triggered). A team that is excluded → 403 `team_ai_excluded`;
 * not my team → 404 `team_not_found`. Not cached.
 */
export const StandupDraftRequestSchema = z
  .object({ teamId: z.string().uuid(), today: LocalDateSchema })
  .strict();
export type StandupDraftRequest = z.infer<typeof StandupDraftRequestSchema>;
export const StandupDraftSchema = z.object({
  teamId: z.string().uuid(),
  today: LocalDateSchema,
  generatedAt: z.string(),
  text: z.string().max(AI_PLANNER_LIMITS.textMax),
});
export type StandupDraft = z.infer<typeof StandupDraftSchema>;

/**
 * GET /api/me/ai/status → MeAiStatus (never 402) — what the ✨ entry points show:
 *  - `ready`          entitled + a personal key (or platform-managed AI) → go;
 *  - `needs_key`      entitled, no personal key → "Add your key" (Personal settings);
 *  - `locked`         not entitled → one locked CTA per surface (§2.6);
 *  - `unavailable`    AI off platform-wide → hide the entry points;
 *  - `quota_reached`  personal AI budget used up this month.
 * D7: `copyableFrom` = teams where I'm OWNER and a team key exists → "Use my
 * key from {Team}" (`POST /api/me/ai/keys/copy-from-team`). Fresh personal
 * keys go through the existing BYOK route on my personal team.
 */
export const MeAiStateSchema = z.enum([
  'ready',
  'needs_key',
  'locked',
  'unavailable',
  'quota_reached',
]);
export type MeAiState = z.infer<typeof MeAiStateSchema>;
export const MeAiStatusSchema = z.object({
  state: MeAiStateSchema,
  personalTeamId: z.string().uuid().nullable(),
  /** A key for this provider exists in my PERSONAL vault. */
  keys: z.object({ anthropic: z.boolean(), openai: z.boolean(), openrouter: z.boolean() }),
  copyableFrom: z.array(
    z.object({
      teamId: z.string().uuid(),
      teamName: z.string(),
      providers: z.array(ByokProvider).min(1),
    }),
  ),
});
export type MeAiStatus = z.infer<typeof MeAiStatusSchema>;

/**
 * POST /api/me/ai/keys/copy-from-team (Idempotency-Key) → MeAiStatus. D7:
 * copies the team's encrypted key for `provider` into my personal vault
 * (re-encrypted, never returned). OWNER of that team only; anything else →
 * 404 `team_key_not_found` (never 403, no oracle).
 */
export const CopyTeamAiKeySchema = z
  .object({ teamId: z.string().uuid(), provider: ByokProvider })
  .strict();
export type CopyTeamAiKeyInput = z.infer<typeof CopyTeamAiKeySchema>;

/** Planner error codes, on top of the AI ones (`ai_byok_required`, `ai_unavailable`, …). */
export const AiPlannerErrorCodes = {
  proposalNotFound: 'plan_proposal_not_found',
  proposalInProgress: 'plan_proposal_in_progress',
  proposalIdempotencyConflict: 'plan_proposal_idempotency_conflict',
  proposalStorageUnavailable: 'plan_proposal_storage_unavailable',
  teamAiExcluded: 'team_ai_excluded',
  teamKeyNotFound: 'team_key_not_found',
} as const;

// ── 2. App ⇄ AI core wire contract (snake_case) ─────────────────────────────

/**
 * JWT `feature` claim per call (HS256, iss=projoint, aud=ai-core, sub=me,
 * tenant=MY PERSONAL team id, scope below, key_source byok|managed, api_keys
 * from the PERSONAL vault only, no user_pat). The Python route rejects a
 * feature mismatch (403 `ai_unauthorized`).
 */
export const AI_CORE_PLANNER_FEATURES = {
  planDay: 'plan_day',
  weeklyReview: 'weekly_review',
  standupDraft: 'standup_draft',
  digest: 'digest',
} as const;
export type AiCorePlannerFeature =
  (typeof AI_CORE_PLANNER_FEATURES)[keyof typeof AI_CORE_PLANNER_FEATURES];

export const AI_CORE_PLANNER_ROUTES: Record<AiCorePlannerFeature, string> = {
  plan_day: '/v1/plan-day',
  weekly_review: '/v1/weekly-review',
  standup_draft: '/v1/standup-draft',
  digest: '/v1/digest',
};

/** JWT `scope` claim; AI core accepts it or the umbrella `ai.use`. */
export const AI_CORE_PLANNER_SCOPES: Record<AiCorePlannerFeature, readonly string[]> = {
  plan_day: ['ai.use_planner'],
  weekly_review: ['ai.use_planner'],
  standup_draft: ['ai.use_standup'],
  digest: ['ai.use_digest'],
};

/** App-side HTTP timeout for these routes (AI core's own hard cap is 5 s lower). */
export const AI_CORE_PLANNER_TIMEOUT_MS: Record<AiCorePlannerFeature, number> = {
  plan_day: 30_000,
  weekly_review: 20_000,
  standup_draft: 15_000,
  digest: 20_000,
};

/** `usage` block of every AI core response (same as `/v1/digest`). */
export const AiCoreUsageSchema = z.object({
  input_tokens: z.number().int().min(0),
  output_tokens: z.number().int().min(0),
  cached_tokens: z.number().int().min(0),
  model: z.string(),
  cost_usd: z.number().min(0),
  latency_ms: z.number().int().min(0),
});
export type AiCoreUsage = z.infer<typeof AiCoreUsageSchema>;

/** Error body (FastAPI `HTTPException(detail=…)`); status ↔ code as in `ai-bridge.service.ts`. */
export const AiCoreErrorSchema = z.object({
  detail: z.object({ code: z.string(), reason: z.string() }),
});

const Locale = z.string().min(2).max(8);
const TimeZone = z.string().min(1).max(64);
const FirstName = z.string().min(1).max(80);
/** "WEB-45" / "ME-12". */
const TaskKey = z.string().min(1).max(24);
const Title = z.string().min(1).max(200);
/** "Acme · Website"; "Personal" for my own space. */
const ProjectLabel = z.string().min(1).max(120);

// POST /v1/plan-day ───────────────────────────────────────────────────────────

export const AiCorePlanDayCandidateSchema = z
  .object({
    task_id: z.string().uuid(),
    key: TaskKey,
    title: Title,
    project: ProjectLabel,
    is_personal: z.boolean(),
    /** The team deadline as a local date in `timezone`; null = none. */
    deadline: LocalDateSchema.nullable(),
    /** 1 = urgent … 4 = low. */
    priority: z.number().int().min(1).max(4),
    status_category: z.enum(['open', 'in_progress', 'review']),
    /** round(laborHours × 60); null = not estimated. */
    estimate_minutes: z.number().int().min(0).nullable(),
    /** max(0, estimate − spent); null = not estimated. */
    remaining_minutes: z.number().int().min(0).nullable(),
    blocked: z.boolean(),
    /** My current plan; null = in My Work but not planned. */
    plan: z
      .object({
        scope: PlanScopeSchema,
        plan_date: LocalDateSchema.nullable(),
        rollover_count: z.number().int().min(0),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type AiCorePlanDayCandidate = z.infer<typeof AiCorePlanDayCandidateSchema>;

export const AiCorePlanDayRequestSchema = z
  .object({
    horizon: PlanHorizonSchema,
    today: LocalDateSchema,
    timezone: TimeZone,
    locale: Locale,
    week_start: WeekStartSchema,
    /** Start of the current week — the only valid `plan_date` for scope `week`. */
    week_start_date: LocalDateSchema,
    /** Days the model may plan into: day → [today]; week → today … week end. */
    days: z.array(LocalDateSchema).min(1).max(7),
    capacity: z.object({ minutes_per_day: z.number().int().min(0).max(1440) }).strict(),
    candidates: z.array(AiCorePlanDayCandidateSchema).min(1).max(AI_PLANNER_LIMITS.candidatesMax),
    max_items: z.number().int().min(1).max(AI_PLANNER_LIMITS.itemsMaxWeek),
  })
  .strict();
export type AiCorePlanDayRequest = z.infer<typeof AiCorePlanDayRequestSchema>;

export const AiCorePlanDayItemSchema = z.object({
  task_id: z.string().uuid(),
  scope: ProposalScopeSchema,
  plan_date: LocalDateSchema.nullable(),
  order: z.number().int().min(0),
  reason: z.string().min(1).max(AI_PLANNER_LIMITS.reasonMax),
});
export type AiCorePlanDayItem = z.infer<typeof AiCorePlanDayItemSchema>;

export const AiCorePlanDayResponseSchema = z.object({
  items: z.array(AiCorePlanDayItemSchema).max(AI_PLANNER_LIMITS.itemsMaxWeek),
  summary: z.string().max(300).nullable(),
  usage: AiCoreUsageSchema,
  prompt_version: z.string(),
});
export type AiCorePlanDayResponse = z.infer<typeof AiCorePlanDayResponseSchema>;

/**
 * The plan-day items the app may show — the SAME rules AI core applies, run
 * again on our side so an invented id can never reach a proposal: each item
 * must parse, name a candidate (at most once), and fit the horizon — `day` on
 * one of `days`, `week` only for horizon week on `week_start_date`, `later`
 * with no date. Invalid items are dropped (never an error); capped at `max_items`.
 */
export function acceptablePlanDayItems(
  req: Pick<
    AiCorePlanDayRequest,
    'horizon' | 'days' | 'week_start_date' | 'candidates' | 'max_items'
  >,
  rawItems: unknown,
): AiCorePlanDayItem[] {
  const ids = new Set(req.candidates.map((c) => c.task_id.toLowerCase()));
  const days = new Set(req.days);
  const seen = new Set<string>();
  const out: AiCorePlanDayItem[] = [];
  for (const raw of Array.isArray(rawItems) ? rawItems : []) {
    const parsed = AiCorePlanDayItemSchema.safeParse(raw);
    if (!parsed.success) continue;
    const item = { ...parsed.data, task_id: parsed.data.task_id.toLowerCase() };
    if (!ids.has(item.task_id) || seen.has(item.task_id)) continue;
    const fits =
      item.scope === 'day'
        ? item.plan_date !== null && days.has(item.plan_date)
        : item.scope === 'week'
          ? req.horizon === 'week' && item.plan_date === req.week_start_date
          : item.plan_date === null;
    if (!fits) continue;
    seen.add(item.task_id);
    out.push(item);
    if (out.length >= req.max_items) break;
  }
  return out;
}

// POST /v1/weekly-review ──────────────────────────────────────────────────────

const AiCoreTaskRefSchema = z
  .object({ key: TaskKey, title: Title, project: ProjectLabel })
  .strict();
const SlippedRef = AiCoreTaskRefSchema.extend({
  rollover_count: z.number().int().min(1),
  deadline: LocalDateSchema.nullable(),
}).strict();
const ProjectHours = z
  .object({
    project: ProjectLabel,
    hours_logged: z.number().min(0),
    done_count: z.number().int().min(0),
  })
  .strict();

export const AiCoreWeeklyReviewRequestSchema = z
  .object({
    user_first_name: FirstName,
    locale: Locale,
    timezone: TimeZone,
    week: z.object({ start: LocalDateSchema, end: LocalDateSchema }).strict(),
    stats: z
      .object({
        done_count: z.number().int().min(0),
        planned_count: z.number().int().min(0),
        planned_done_count: z.number().int().min(0),
        carried_over_count: z.number().int().min(0),
        slipped_count: z.number().int().min(0),
        overdue_count: z.number().int().min(0),
        hours_logged: z.number().min(0),
        hours_planned: z.number().min(0).nullable(),
        upcoming_deadlines_count: z.number().int().min(0),
      })
      .strict(),
    done: z.array(AiCoreTaskRefSchema).max(15),
    slipped: z.array(SlippedRef).max(10),
    upcoming_deadlines: z
      .array(AiCoreTaskRefSchema.extend({ deadline: LocalDateSchema }).strict())
      .max(10),
    by_project: z.array(ProjectHours).max(8),
  })
  .strict();
export type AiCoreWeeklyReviewRequest = z.infer<typeof AiCoreWeeklyReviewRequestSchema>;

/** Response of `/v1/weekly-review` and `/v1/standup-draft`: plain text (short Markdown-lite). */
export const AiCoreTextResponseSchema = z.object({
  text: z.string().min(1).max(AI_PLANNER_LIMITS.textMax),
  usage: AiCoreUsageSchema,
  prompt_version: z.string(),
});
export type AiCoreTextResponse = z.infer<typeof AiCoreTextResponseSchema>;

// POST /v1/standup-draft ──────────────────────────────────────────────────────

const AiCoreTeamTaskSchema = z.object({ key: TaskKey, title: Title }).strict();

export const AiCoreStandupDraftRequestSchema = z
  .object({
    user_first_name: FirstName,
    locale: Locale,
    timezone: TimeZone,
    today: LocalDateSchema,
    /** The ONE team the standup is for; every task below belongs to it. */
    team: z.object({ name: z.string().min(1).max(120) }).strict(),
    yesterday: z
      .object({ date: LocalDateSchema, done: z.array(AiCoreTeamTaskSchema).max(15) })
      .strict(),
    /** Titles of this team's tasks in my plan for today, in plan order. */
    today_plan: z.array(AiCoreTeamTaskSchema).max(15),
    blockers: z
      .array(
        AiCoreTeamTaskSchema.extend({ blocked_by: z.array(AiCoreTeamTaskSchema).max(3) }).strict(),
      )
      .max(10),
  })
  .strict();
export type AiCoreStandupDraftRequest = z.infer<typeof AiCoreStandupDraftRequestSchema>;
